/**
 * Defines the decorator.
 * This decorator is supposed to be applied on properties (without accessor defined.) of the view model.
 * The property applied with this decorator will be added into a map called UI update notification map.
 * Based on this map on the object, a specific UI Binding implementation (e.g. UIBindingForReact) based on specific UI framework can attach an extra behavior
 * when setting the property value for the purpose of notifying the UI framework that a new value is set to this property.
 * But the way how this extra behavior notifies the UI framework doesn't matter here since we don't want to introduce any UI framework dependency here and in the view model.
 * The actual view component based on the UI framework (e.g. React) will call the specific binding setup function to implement the way for notifying view model property changes.
 * Please refer to setupUIBindingForReactFunctionalComponent in UIBindingForReact.ts for React version of binding setup.
 * An optional property update handler can be passed into this BindUI function.
 * This handler will be called when the property value is set.
 * The referred function of this handler has to be a static function. When this handler is called, the object who owns this property will be passed in as well as the value to be set to this property.
 * The actual UI Binding implementation (e.g. setupUIBindingForReactFunctionalComponent) will actually call the handler.
 * Usage:
 * class SampleViewModel {
 *   @BindUI(samplePropertyUpdatedHandler) public sampleProperty: string;
 * }
 */
export function BindUI(propertyUpdatedHandler?: (object: any, value: any) => void) {
    return function decorate(target: any, property: string) {
        const viewModelForUIBinding = target as ViewModelForUIBinding;
        if (!viewModelForUIBinding.__uiUpdateNotificationMap) {
            viewModelForUIBinding.__uiUpdateNotificationMap = {};
        }
        viewModelForUIBinding.__uiUpdateNotificationMap[property] = propertyUpdatedHandler;
    };
}

/**
 * Defines the view model for UI binding.
 * The key part here is the key value pair which stores each of the decorated property and its UI update notification function.
 */
export type ViewModelForUIBinding = {
    __uiUpdateNotificationMap: { [property: string]: (object: any, value: any) => void };
}

